// =====================================================================
// src/lib/v2/storagePhotos.js
//
// V2 Photo Upload Helpers — Phase 2.3 Foundation (Commit 1)
//
// Mirrors the V1 pattern in src/lib/db.js:
//   1. Pick file → validateImageFile() (strict whitelist, no video)
//   2. Pre-flight check{X}Limit() reads quota counter from Firestore
//   3. compressImage() (reused from V1's ./imageCompress) → WebP blob
//   4. uploadBytes() with customMetadata + 1-year immutable cache headers
//   5. bump{X}Count() updates the counter atomically
//
// Why strict (not V1's `file.type.startsWith('image/')`):
//   `image/*` accepts image/gif (animated), image/heic (large, non-canvas-
//   renderable on Android), and several other variants that either misbehave
//   in canvas.toBlob() or smuggle "video-like" payloads. V2 hard-whitelists
//   jpeg/png/webp at the client; storage.rules enforce the same regex.
//
// Cost-control surface:
//   - LOGO + COVER replace in place (same path). Many uploads = same one file
//     in Storage. The monthly cap is UX-driven, not cost-driven; client-side
//     check is sufficient. firestore.rules validates counter shape so client
//     can't fake the month.
//   - PRODUCT IMAGES add a new file per product. Cap IS cost-relevant.
//     storage.rules enforces productImageCount < productImageLimit via a
//     firestore.get() cross-check (one read per upload, uploads are rare).
//   - CHAT PHOTOS add a new file per message. Defensive per-user monthly cap
//     here is client-side; storage.rules block non-participants.
//   - FSSAI cert is one-time per fssaiVerified=false cycle. firestore.rules
//     block update once fssaiVerified flips true.
//
// Limit overrides (super-admin path, no UI yet):
//   Each business doc may carry optional fields read here as soft overrides:
//     - productImageLimit          (default 25)
//     - logoChangesLimitMonthly    (default 3)
//     - coverChangesLimitMonthly   (default 3)
//   firestore.rules block owner from writing these fields. Admin writes
//   them directly via Firebase Console or a future super-admin tool.
//
// Future: a Cloud Function on Storage onFinalize could re-credit counters
// server-side using customMetadata.ownerUid (same pattern V1 left hooks for).
// Deliberately skipped for V2 first cut to avoid bumping Firebase bill.
// =====================================================================

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, getDoc, setDoc, increment } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { compressImage, detectAspectRatio } from '../imageCompress';

// =====================================================================
// CONSTANTS
// =====================================================================

// Strict whitelist. Storage rules use the same regex (see storage.rules).
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// Raw file cap BEFORE compression. Matches V1 (App.jsx L3177, L2465).
// Modern phones routinely produce 8-12 MB JPEGs; this is the absolute ceiling.
const MAX_RAW_BYTES = 25 * 1024 * 1024;

// Cache header — same as V1. One year, immutable so browsers + Storage CDN
// can cache aggressively. Filenames are stable (logo.webp etc.) — when the
// owner replaces a logo, the new bytes overwrite the same path; callers
// append a `?t=${Date.now()}` cache-buster when reading the URL.
const CACHE_CONTROL_IMMUTABLE = 'public, max-age=31536000, immutable';

// Default quotas. Each can be overridden per-business via Firestore doc field.
const DEFAULT_LIMITS = {
  logoChangesLimitMonthly: 3,
  coverChangesLimitMonthly: 3,
  productImageLimit: 25,
  chatPhotosLimitMonthly: 200, // defensive per-user cap; not user-facing
};

// =====================================================================
// SHARED UTILS
// =====================================================================

/**
 * Strict client-side validation. Throws on bad input.
 * First line of defense; storage.rules is the second.
 *
 * Rejects:
 *   - Missing file
 *   - Type outside jpeg/png/webp (catches video/*, image/gif, image/heic, etc.)
 *   - Files larger than MAX_RAW_BYTES pre-compression
 *   - Empty files (file.size === 0 — sometimes happens with broken pickers)
 */
function validateImageFile(file) {
  if (!file) throw new Error('No image selected');
  if (!file.type || !ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Only JPEG, PNG, or WebP images are supported');
  }
  if (file.size === 0) throw new Error('Image file is empty — try picking it again');
  if (file.size > MAX_RAW_BYTES) {
    throw new Error('Image must be under 25 MB. Try a smaller photo.');
  }
}

/**
 * YYYYMM string for current month. Duplicate of V1's currentYYYYMM (db.js L1469)
 * which is unexported. Keep the duplication; tiny, and decoupling V2 helpers
 * from V1 internals is intentional.
 */
function currentYYYYMM() {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Read business doc with a single getDoc. Throws if business doesn't exist.
 * Used by every check*Limit helper. firestore.rules already restricts reads
 * to verified/pending/owner — caller errors propagate as a clear "permission
 * denied" if e.g. an unauthorized user calls a check helper.
 */
async function getBusinessData(bizId) {
  if (!bizId) throw new Error('bizId required');
  const snap = await getDoc(doc(db, 'businesses', bizId));
  if (!snap.exists()) throw new Error('Business not found');
  return snap.data();
}

/**
 * Read user doc for chat-photo counter. Fail-open: if user doc missing
 * (shouldn't happen mid-session) treat as zero used.
 */
async function getUserData(userId) {
  if (!userId) throw new Error('userId required');
  const snap = await getDoc(doc(db, 'users', userId));
  return snap.exists() ? snap.data() : {};
}

// =====================================================================
// LOGO + COVER UPLOADS (replace-in-place, UX-capped at 3/month)
// =====================================================================
// Both functions follow the same shape: validate → check limit → compress
// → upload to fixed path → bump counter. Logo and cover are independent
// quotas (3 logo changes AND 3 cover changes per month) — owner can change
// both up to 6 total per month.

/**
 * Check whether owner is under their monthly logo-change cap.
 * Returns { allowed, used, limit, remaining }. Fail-open on error
 * (same pattern as V1's checkPostPhotoLimit) — don't block legit users
 * if Firestore hiccups.
 */
export async function checkBusinessLogoLimit(bizId) {
  try {
    const data = await getBusinessData(bizId);
    const limit = data.logoChangesLimitMonthly || DEFAULT_LIMITS.logoChangesLimitMonthly;
    const counter = data.logoChangesThisMonth || {};
    const ym = currentYYYYMM();
    const used = (counter.yyyymm === ym) ? (counter.count || 0) : 0;
    return {
      allowed: used < limit,
      used,
      limit,
      remaining: Math.max(0, limit - used),
    };
  } catch (e) {
    console.warn('Could not check business logo limit:', e);
    return { allowed: true, used: 0, limit: DEFAULT_LIMITS.logoChangesLimitMonthly, remaining: DEFAULT_LIMITS.logoChangesLimitMonthly };
  }
}

/**
 * Upload a business logo. Replaces existing at the same path.
 * Path: businesses/{bizId}/logo.webp
 *
 * Caller is expected to:
 *   1. Call checkBusinessLogoLimit() first and show a friendly message
 *      if !allowed, before invoking this.
 *   2. Call bumpBusinessLogoCount() AFTER this returns successfully.
 *   3. Persist the returned URL to businesses/{bizId}.logoImageUrl
 *      via the existing business-doc update path.
 */
export async function uploadBusinessLogo(bizId, file, ownerUid) {
  if (!bizId) throw new Error('bizId required');
  if (!ownerUid) throw new Error('ownerUid required for logo upload metadata');
  validateImageFile(file);

  const compressed = await compressImage(file);
  const path = `businesses/${bizId}/logo.webp`;
  const photoRef = ref(storage, path);

  // customMetadata.ownerUid future-proofs for a Cloud Function that wants
  // to credit the owner without a Firestore lookup. Cheap to include now.
  await uploadBytes(photoRef, compressed, {
    contentType: 'image/webp',
    cacheControl: CACHE_CONTROL_IMMUTABLE,
    customMetadata: { ownerUid, kind: 'business_logo' },
  });

  return await getDownloadURL(photoRef);
}

/** Increment the monthly logo-change counter. Call after a successful upload. */
export async function bumpBusinessLogoCount(bizId) {
  if (!bizId) return;
  try {
    const data = await getBusinessData(bizId);
    const counter = data.logoChangesThisMonth || {};
    const ym = currentYYYYMM();
    const newCount = (counter.yyyymm === ym) ? (counter.count || 0) + 1 : 1;
    await setDoc(
      doc(db, 'businesses', bizId),
      { logoChangesThisMonth: { yyyymm: ym, count: newCount } },
      { merge: true }
    );
  } catch (e) {
    console.warn('Could not bump business logo count:', e);
  }
}

/** Check monthly cover-change cap. Same shape as the logo helper. */
export async function checkBusinessCoverLimit(bizId) {
  try {
    const data = await getBusinessData(bizId);
    const limit = data.coverChangesLimitMonthly || DEFAULT_LIMITS.coverChangesLimitMonthly;
    const counter = data.coverChangesThisMonth || {};
    const ym = currentYYYYMM();
    const used = (counter.yyyymm === ym) ? (counter.count || 0) : 0;
    return {
      allowed: used < limit,
      used,
      limit,
      remaining: Math.max(0, limit - used),
    };
  } catch (e) {
    console.warn('Could not check business cover limit:', e);
    return { allowed: true, used: 0, limit: DEFAULT_LIMITS.coverChangesLimitMonthly, remaining: DEFAULT_LIMITS.coverChangesLimitMonthly };
  }
}

/**
 * Upload a business cover photo. Replaces existing at the same path.
 * Path: businesses/{bizId}/cover.webp
 */
export async function uploadBusinessCover(bizId, file, ownerUid) {
  if (!bizId) throw new Error('bizId required');
  if (!ownerUid) throw new Error('ownerUid required for cover upload metadata');
  validateImageFile(file);

  const compressed = await compressImage(file);
  const path = `businesses/${bizId}/cover.webp`;
  const photoRef = ref(storage, path);

  await uploadBytes(photoRef, compressed, {
    contentType: 'image/webp',
    cacheControl: CACHE_CONTROL_IMMUTABLE,
    customMetadata: { ownerUid, kind: 'business_cover' },
  });

  return await getDownloadURL(photoRef);
}

/** Increment the monthly cover-change counter. Call after a successful upload. */
export async function bumpBusinessCoverCount(bizId) {
  if (!bizId) return;
  try {
    const data = await getBusinessData(bizId);
    const counter = data.coverChangesThisMonth || {};
    const ym = currentYYYYMM();
    const newCount = (counter.yyyymm === ym) ? (counter.count || 0) + 1 : 1;
    await setDoc(
      doc(db, 'businesses', bizId),
      { coverChangesThisMonth: { yyyymm: ym, count: newCount } },
      { merge: true }
    );
  } catch (e) {
    console.warn('Could not bump business cover count:', e);
  }
}

// =====================================================================
// PRODUCT IMAGE UPLOADS (slot-based, cost-relevant cap)
// =====================================================================
// Unlike logo/cover, each product image lives at a UNIQUE path per product:
//   businesses/{bizId}/products/{productId}.webp
// Adding more products adds more files; this is the only cost-relevant
// growth vector under businesses/. The cap is enforced server-side via
// storage.rules cross-checking productImageCount on the business doc.
//
// Counter semantics:
//   productImageCount is a LIFETIME counter (not monthly).
//   - Upload a new product image → bumpProductImageCount() (+1)
//   - Delete a product image     → decrementProductImageCount() (-1)
//   - Replace existing image at same productId → no counter change
//     (path replacement; storage.rules don't re-check on overwrite of
//     same path because the doc's count hasn't moved past limit)
// Default cap: 25. Overridable per business via productImageLimit.

/**
 * Check whether the business has remaining product-image slots.
 * Returns { allowed, used, limit, remaining }.
 */
export async function checkProductImageQuota(bizId) {
  try {
    const data = await getBusinessData(bizId);
    const limit = data.productImageLimit || DEFAULT_LIMITS.productImageLimit;
    const used = data.productImageCount || 0;
    return {
      allowed: used < limit,
      used,
      limit,
      remaining: Math.max(0, limit - used),
    };
  } catch (e) {
    console.warn('Could not check product image quota:', e);
    return { allowed: true, used: 0, limit: DEFAULT_LIMITS.productImageLimit, remaining: DEFAULT_LIMITS.productImageLimit };
  }
}

/**
 * Upload a product image. NEW slot consumes quota; replace at same productId
 * does not.
 * Path: businesses/{bizId}/products/{productId}.webp
 *
 * isNew: pass true when this is a fresh product (first upload at this
 * productId), false when replacing an existing product's image. Caller
 * is responsible for this distinction — storage.rules also enforce the
 * cap, so a malicious client claiming `isNew=false` while the productId
 * is new just fails server-side.
 */
export async function uploadProductImage(bizId, productId, file, ownerUid) {
  if (!bizId) throw new Error('bizId required');
  if (!productId) throw new Error('productId required');
  if (!ownerUid) throw new Error('ownerUid required for product image metadata');
  validateImageFile(file);

  const compressed = await compressImage(file);
  const path = `businesses/${bizId}/products/${productId}.webp`;
  const photoRef = ref(storage, path);

  await uploadBytes(photoRef, compressed, {
    contentType: 'image/webp',
    cacheControl: CACHE_CONTROL_IMMUTABLE,
    customMetadata: { ownerUid, kind: 'product_image' },
  });

  return await getDownloadURL(photoRef);
}

/**
 * Delete a product image and decrement the counter.
 * Call when the merchant removes a product entirely (not when replacing
 * the image of an existing product).
 */
export async function deleteProductImage(bizId, productId) {
  if (!bizId || !productId) return;
  try {
    await deleteObject(ref(storage, `businesses/${bizId}/products/${productId}.webp`));
  } catch (e) {
    // 404 (object-not-found) is fine — product may have never had an image.
    if (e?.code !== 'storage/object-not-found') {
      console.warn('Could not delete product image:', e);
    }
  }
}

/**
 * Increment lifetime product-image counter on the business doc. Call after
 * a NEW product image upload (not on replace).
 */
export async function bumpProductImageCount(bizId) {
  if (!bizId) return;
  try {
    await setDoc(
      doc(db, 'businesses', bizId),
      { productImageCount: increment(1) },
      { merge: true }
    );
  } catch (e) {
    console.warn('Could not bump product image count:', e);
  }
}

/**
 * Decrement lifetime product-image counter. Call after deleteProductImage()
 * when the product is removed entirely.
 */
export async function decrementProductImageCount(bizId) {
  if (!bizId) return;
  try {
    await setDoc(
      doc(db, 'businesses', bizId),
      { productImageCount: increment(-1) },
      { merge: true }
    );
  } catch (e) {
    console.warn('Could not decrement product image count:', e);
  }
}

// =====================================================================
// CHAT PHOTO UPLOADS (per-user defensive monthly cap)
// =====================================================================
// Single photo per message, no video. Cap is per-user (sender), not per-
// business or per-order, because spam is a sender concern. Cap stored on
// users/{uid}.chatPhotosThisMonth. Default 200/month — defensive, not
// user-facing. Real anti-spam comes from storage.rules participant check.

export async function checkChatPhotoLimit(userId) {
  try {
    const data = await getUserData(userId);
    const counter = data.chatPhotosThisMonth || {};
    const ym = currentYYYYMM();
    const used = (counter.yyyymm === ym) ? (counter.count || 0) : 0;
    const limit = DEFAULT_LIMITS.chatPhotosLimitMonthly;
    return {
      allowed: used < limit,
      used,
      limit,
      remaining: Math.max(0, limit - used),
    };
  } catch (e) {
    console.warn('Could not check chat photo limit:', e);
    return { allowed: true, used: 0, limit: DEFAULT_LIMITS.chatPhotosLimitMonthly, remaining: DEFAULT_LIMITS.chatPhotosLimitMonthly };
  }
}

/**
 * Upload a single chat photo (customer or merchant side).
 * Path: chats/{orderId}/{messageId}.webp
 *
 * storage.rules verify the sender is either the order's customer OR the
 * owner of the business referenced by the order's businessId.
 * Returns { url, aspect } — same shape as V1's uploadPostPhoto / uploadMemoryPhoto.
 */
export async function uploadChatPhoto(orderId, messageId, file, senderUid) {
  if (!orderId) throw new Error('orderId required');
  if (!messageId) throw new Error('messageId required');
  if (!senderUid) throw new Error('senderUid required for chat photo metadata');
  validateImageFile(file);

  const aspect = await detectAspectRatio(file);
  const compressed = await compressImage(file);
  const path = `chats/${orderId}/${messageId}.webp`;
  const photoRef = ref(storage, path);

  await uploadBytes(photoRef, compressed, {
    contentType: 'image/webp',
    cacheControl: CACHE_CONTROL_IMMUTABLE,
    customMetadata: { senderUid, kind: 'chat_photo', orderId },
  });

  const url = await getDownloadURL(photoRef);
  return { url, aspect };
}

export async function bumpChatPhotoCount(userId) {
  if (!userId) return;
  try {
    const data = await getUserData(userId);
    const counter = data.chatPhotosThisMonth || {};
    const ym = currentYYYYMM();
    const newCount = (counter.yyyymm === ym) ? (counter.count || 0) + 1 : 1;
    await setDoc(
      doc(db, 'users', userId),
      { chatPhotosThisMonth: { yyyymm: ym, count: newCount } },
      { merge: true }
    );
  } catch (e) {
    console.warn('Could not bump chat photo count:', e);
  }
}

// =====================================================================
// WALL POST IMAGES (no quota — Phase 2.5 / C10)
// =====================================================================
// Path: businesses/{bizId}/posts/{postId}/{index}.webp  (index = 0, 1, 2)
//
// Wall posts allow up to 3 photos. Filenames are indexed (not random)
// so callers can re-upload to overwrite specific slots when editing,
// and so deleteWallPostImages can iterate without listing.
//
// No lifetime counter (unlike product images) — wall posts are
// marketing content where high cadence is good signal, not abuse.
// Storage cost is bounded by typical merchant posting (~18 MB/yr per
// active merchant). If abuse surfaces, add a per-month counter mirroring
// the chat photo pattern.
//
// storage.rules enforces owner-only writes + image type + size cap.
// =====================================================================

export async function uploadWallPostImage(bizId, postId, file, ownerUid, index) {
  if (!bizId) throw new Error('bizId required');
  if (!postId) throw new Error('postId required');
  if (!ownerUid) throw new Error('ownerUid required for wall post image metadata');
  if (typeof index !== 'number' || index < 0 || index > 2) {
    throw new Error('index must be 0, 1, or 2 (max 3 photos per post)');
  }
  validateImageFile(file);

  const compressed = await compressImage(file);
  const path = `businesses/${bizId}/posts/${postId}/${index}.webp`;
  const photoRef = ref(storage, path);

  await uploadBytes(photoRef, compressed, {
    contentType: 'image/webp',
    cacheControl: CACHE_CONTROL_IMMUTABLE,
    customMetadata: { ownerUid, kind: 'wall_post_image', postId, photoIndex: String(index) },
  });

  return await getDownloadURL(photoRef);
}

/**
 * Delete one specific wall post photo (used when editing — owner removes
 * a single photo from a post that had multiple). Pass the same index used
 * during upload.
 */
export async function deleteWallPostImage(bizId, postId, index) {
  if (!bizId || !postId) return;
  if (typeof index !== 'number' || index < 0 || index > 2) return;
  try {
    await deleteObject(ref(storage, `businesses/${bizId}/posts/${postId}/${index}.webp`));
  } catch (e) {
    if (e?.code !== 'storage/object-not-found') {
      console.warn(`Could not delete wall post photo ${index}:`, e);
    }
  }
}

/**
 * Delete ALL photos for a wall post. Called when the post itself is
 * deleted. Iterates 0..2 — cheap, photos are bounded at 3 per post.
 */
export async function deleteAllWallPostImages(bizId, postId) {
  if (!bizId || !postId) return;
  for (let i = 0; i < 3; i++) {
    await deleteWallPostImage(bizId, postId, i);
  }
}

// =====================================================================
// FSSAI CERTIFICATE UPLOAD (one-time per verification cycle)
// =====================================================================
// Food businesses upload an FSSAI license certificate image. firestore.rules
// already block fssaiNumber changes once fssaiVerified=true (L527); the cert
// URL field follows the same lock. Replacing the cert requires admin to
// flip fssaiVerified back to false (a re-verification trigger).

/**
 * Upload an FSSAI certificate image.
 * Path: businesses/{bizId}/fssai.webp
 *
 * No monthly cap — this is a one-time-per-cycle upload. storage.rules
 * additionally enforce fssaiVerified == false before allowing write.
 */
export async function uploadFssaiCert(bizId, file, ownerUid) {
  if (!bizId) throw new Error('bizId required');
  if (!ownerUid) throw new Error('ownerUid required for FSSAI cert metadata');
  validateImageFile(file);

  const compressed = await compressImage(file);
  const path = `businesses/${bizId}/fssai.webp`;
  const photoRef = ref(storage, path);

  await uploadBytes(photoRef, compressed, {
    contentType: 'image/webp',
    cacheControl: CACHE_CONTROL_IMMUTABLE,
    customMetadata: { ownerUid, kind: 'fssai_cert' },
  });

  return await getDownloadURL(photoRef);
}

// =====================================================================
// EXPORTS — validation helpers, in case UI wants to pre-validate
// =====================================================================

export { validateImageFile, ALLOWED_TYPES, MAX_RAW_BYTES, DEFAULT_LIMITS };
